import { createSlice } from '@reduxjs/toolkit'
import { isSourceFill } from '~/lib/sourceFill'
import initialState from './initial'

export const editorSlice = createSlice({
	name: 'editor',
	initialState,
	reducers: {
		expandLayer: (s, { payload }) => {
			const { id, expand } = payload
			if (expand) {
				if (!s.expanded_layer_ids.includes(id)) s.expanded_layer_ids.push(id)
			} else {
				const index = s.expanded_layer_ids.indexOf(id)
				if (index !== -1) {
					s.expanded_layer_ids.splice(index, 1)
				}
			}
		},
		setCurrentFill: (s, { payload }) => {
			if (payload && typeof payload === 'object') return
			s.current_fill = payload
		},
		/** The source-filled shape a Sources pick refills when nothing fill-able is selected. */
		setLastFillId: (s, { payload }) => {
			s.last_fill_id = payload
		},
		setCurrentStroke: (s, { payload }) => {
			s.current_stroke = payload
		},
		setCurrentTool: (s, { payload }) => {
			s.current_tool = payload
		},
		setBrushWidth: (s, { payload }) => {
			s.current_brush_width = payload
		},
		setBrushCap: (s, { payload }) => {
			s.current_brush_cap = payload
		},
		setSmoothness: (s, { payload }) => {
			s.current_smoothness = payload
		},
		setMinWidth: (s, { payload }) => {
			s.current_min_width = payload
		},
		setWidthProfile: (s, { payload }) => {
			s.current_width_profile = payload
		},
		setAttraction: (s, { payload }) => {
			s.current_attraction = payload
		},
		setPreserveLength: (s, { payload }) => {
			s.rope_mode = payload
		},
		setRopeBend: (s, { payload }) => {
			s.rope_bend = payload
		},
		setEraserWidth: (s, { payload }) => {
			s.current_eraser_width = payload
		},
		setEraserSmoothness: (s, { payload }) => {
			s.current_eraser_smoothness = payload
		},
		setExpandedLayerIds: (s, { payload }) => {
			s.expanded_layer_ids = payload
		},
		setIsMasking: (s, { payload }) => {
			s.is_masking = payload
		},
		setIsTransforming: (s, { payload }) => {
			s.is_transforming = payload
		},
		enterGroup: (s, { payload }) => {
			s.editing_group_stack.push(payload)
		},
		exitGroup: s => {
			s.editing_group_stack.pop()
		},
		exitToGroup: (s, { payload }) => {
			const idx = s.editing_group_stack.indexOf(payload)
			if (idx >= 0) s.editing_group_stack.splice(idx + 1)
			else s.editing_group_stack = []
		},
		setEditingTextId: (s, { payload }) => {
			s.editing_text_id = payload
		},
		setFontFamily: (s, { payload }) => {
			s.current_font_family = payload
		},
		setFontSize: (s, { payload }) => {
			s.current_font_size = payload
		},
		setBold: (s, { payload }) => {
			s.current_bold = payload
		},
		setItalic: (s, { payload }) => {
			s.current_italic = payload
		},
		setAllCaps: (s, { payload }) => {
			s.current_all_caps = payload
		},
		setTextAlign: (s, { payload }) => {
			s.current_text_align = payload
		},
		setLetterSpacing: (s, { payload }) => {
			s.current_letter_spacing = payload
		},
		setLineHeight: (s, { payload }) => {
			s.current_line_height = payload
		},
	},
	extraReducers: b => {
		b.addCase('live/addObjects', s => {
			if (s.current_tool !== 'brush' && s.current_tool !== 'eraser' && s.current_tool !== 'add_text') {
				s.current_tool = 'move'
			}
		})
		b.addCase('live/deleteObjects', (s, { payload }) => {
			s.editing_group_stack = []
			if (s.editing_text_id && payload.includes(s.editing_text_id)) {
				s.editing_text_id = null
			}
		})
		b.addCase('live/applySourceFill', (s, { payload }) => {
			if (isSourceFill(payload.fill) && payload.ids.length === 1) s.last_fill_id = payload.ids[0]
		})
		b.addCase('live/updateObject', (s, { payload }) => {
			if (payload.id === 'root') return
			const { fill, stroke } = payload
			if (isSourceFill(fill)) s.last_fill_id = payload.id
			else if (fill) s.current_fill = fill
			if (stroke) s.current_stroke = stroke
			if (payload.fontFamily) s.current_font_family = payload.fontFamily
			if (payload.fontSize) s.current_font_size = payload.fontSize
			if ('bold' in payload) s.current_bold = payload.bold
			if ('italic' in payload) s.current_italic = payload.italic
			if ('allCaps' in payload) s.current_all_caps = payload.allCaps
			if (payload.textAlign) s.current_text_align = payload.textAlign
			if ('letterSpacing' in payload) s.current_letter_spacing = payload.letterSpacing
			if ('lineHeight' in payload) s.current_line_height = payload.lineHeight
		})
		// Only what an `objects` patch deletes (`null`) goes: a peer's edit, or the echo of a local one, must not collapse layers or leave a group.
		b.addCase('live/patchLiveObject', (s, { payload }) => {
			const objects = payload.objects
			if (!objects) return
			const gone = id => objects[id] === null
			if (s.expanded_layer_ids.some(gone)) s.expanded_layer_ids = s.expanded_layer_ids.filter(id => !gone(id))
			const i = s.editing_group_stack.findIndex(gone)
			if (i >= 0) s.editing_group_stack.splice(i)
		})
	},
})

export const {
	enterGroup,
	exitGroup,
	exitToGroup,
	expandLayer,
	setAllCaps,
	setAttraction,
	setBold,
	setBrushCap,
	setBrushWidth,
	setCurrentFill,
	setLastFillId,
	setCurrentStroke,
	setCurrentTool,
	setEditingTextId,
	setEraserSmoothness,
	setEraserWidth,
	setExpandedLayerIds,
	setFontFamily,
	setFontSize,
	setIsMasking,
	setIsTransforming,
	setItalic,
	setLetterSpacing,
	setLineHeight,
	setMinWidth,
	setPreserveLength,
	setRopeBend,
	setSmoothness,
	setTextAlign,
	setWidthProfile,
} = editorSlice.actions

export default editorSlice.reducer
